import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { p, tp, useP } from '../../i18n/useP';
import { useTranslation } from 'react-i18next';
import { usePersistentValue, usePersistentValueLocal } from '../../hooks/usePersistentState';
import {
  listHrContracts, listHrPeople, listHrLppRates, peopleCostSynthesis,
  listPayrollMovements, createPayrollMovement, updatePayrollMovement,
  deletePayrollMovement,
} from '../../api/hr';
import { formatNumber, formatMoney } from '../../utils/format';
import { normalizeName, personIsActive, buildSearchKey } from '../../utils/personTable';
import { useTableSort } from '../../hooks/useTableSort';
import { cellFteStyle, fteColor } from './fteColor';
import { addExtraSalariesToCells, smoothExtraSalariesToCells, extraMonths } from './salaryMass';
/* Moteur du plan, PARTAGÉ avec la Synthèse des effectifs (2026-08-29) : ces
   fonctions vivaient ici, elles vivent maintenant à côté. Deux écrans qui
   projettent le même effectif doivent le faire avec le même code. */
import {
  monthStartOf, pickReferenceContract, buildBaseCells,
  applyPersonMovements, buildSyntheticRow, logicalType,
} from './planningEngine';
import PageHeader from '../../components/PageHeader';
import PersonFilterBar from '../../components/PersonFilterBar';
import SegmentedToggle from '../../components/SegmentedToggle';
import CardShell from '../../components/CardShell';
import SearchField from '../../components/SearchField';
import PeriodNav from '../../components/PeriodNav';
import FilterIcon from '../../components/FilterIcon';
import FilterCount from '../../components/FilterCount';
import PlanningTimeline from './PlanningTimeline';
import PayrollVariationChart from './PayrollVariationChart';
import PeristyleToggle from '../../components/PeristyleToggle';
import PersonName from '../../components/PersonName';
import PersonTh from '../../components/PersonTh';
import '../../components/UnitSwitch.css';
import './HrPage.css';

/**
 * Personnel planning — clone éditable de Year-end extrapolation.
 *
 * Year-end reste en lecture seule (réalité contractuelle, LPP exacte). Cette
 * page superpose des MOUVEMENTS hypothétiques (table payroll_simulation_movements)
 * sur la baseline contrats, pour bâtir un prévisionnel :
 *   - hire        : position ouverte (sans person_id) → ligne synthétique +
 *   - departure   : départ planifié (person_id) → éteint la ligne de la personne
 *                   · réduction non nommée (sans person_id) → ligne synthétique −
 *   - rate_change : changement de taux d'une personne existante (person_id),
 *                   taux ABSOLU dans `fte`, sur [De, À] (À vide = permanent).
 *
 * Horizon N→N+1 couvert par le sélecteur d'année (topbar) : une page par année,
 * les mouvements ont des dates absolues et persistent d'une année à l'autre.
 */

/* Months via common:months / monthsFull in the page component. */
const ALL_M = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
const DEFAULT_AMOUNT = 10000;

/* ── Helpers dates ───────────────────────────────────────────────────────── */

function parseLocalDate(s) {
  if (!s) return null;
  const [y, m, d] = String(s).slice(0, 10).split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}
function fmtMonthYear(s) {
  if (!s) return '—';
  const d = parseLocalDate(s);
  return d ? `${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}` : '—';
}
function ymToDateStr(year, monthIdx) {
  return `${year}-${String(monthIdx + 1).padStart(2, '0')}-01`;
}
// Décale une date 'YYYY-MM-01' de n mois (gère le passage d'année).
function addMonthsStr(s, n) {
  const d = parseLocalDate(s);
  if (!d) return s;
  const nd = new Date(d.getFullYear(), d.getMonth() + n, 1);
  return ymToDateStr(nd.getFullYear(), nd.getMonth());
}


// Étale les extras annuels (compléments soumis CS : primes, bonus, jetons) d'une
// personne sur ses mois actifs, AU PRORATA DU TAUX (EPT) planifié. Au taux de
// référence (baseline), Σ = annualExtras ; une baisse/hausse de taux ajuste les
// extras d'autant — le coût-par-EPT inclut donc base + extras. Source unique :
// bonus_variable de la synthèse People (= onglet Personnel actuel).
function addExtrasToCells(cells, annualExtras, refRate, nBaseActive) {
  if (!annualExtras || !refRate || nBaseActive <= 0) return cells;
  const perActive = annualExtras / nBaseActive; // extras d'un mois actif au taux ref
  return cells.map(c => {
    const f = (c.pct || 0) / 100;
    return f > 0 ? { ...c, gross: (c.gross || 0) + perActive * (f / refRate) } : c;
  });
}

const fmtChfInt = (n) => Math.round(Number(n) || 0).toLocaleString('de-CH').replace(/\./g, "'");

// Colonnes triables de la grille (les 12 mois ne sont pas triables) —
// même pattern que Year-end extrapolation. La colonne personne porte
// `personSort` : tri via pastilles P/N (first_name_sort / last_name_sort).
const SORT_COLUMNS = [
  { key: 'person',           label: tp('phrases:p_984f5046d6'), align: 'left', personSort: true },
  { key: 'department',       label: tp('phrases:p_3d7c87c2af'),  align: 'left',  detail: true },
  { key: 'salary100',        label: p('100% (CHF/m)'), align: 'right', detail: true },
  { key: 'entry_date',       label: tp('phrases:p_0ab59cb968'),       align: 'left',  detail: true },
  { key: 'termination_date', label: 'Sortie',       align: 'left',  detail: true },
];


const LOGICAL = {
  hire:          { label: 'Position ouverte', mt: 'hire',       person: false, rate: true,  amount: true,  end: true  },
  cut:           { label: tp('phrases:p_b2a060ca5d'), mt: 'departure',  person: false, rate: true,  amount: true,  end: true  },
  exit:          { label: p('Fin de contrat'),   mt: 'departure',  person: true,  rate: false, amount: false, end: false },
  rate_change:   { label: p('Chgt de taux'),     mt: 'rate_change', person: true, rate: true,  amount: false, end: true  },
  // Delta signé (+/-), pas de taux — voir applyPersonMovements.
  salary_change: { label: p('Modification du salaire'), mt: 'salary_change', person: true, rate: false, amount: true, end: true, signed: true },
};

/* ── Page ────────────────────────────────────────────────────────────────── */

/* `part` — la page sert DEUX onglets de la section « Effectif » (2026-08-27) :
     'plan'  = les tuiles annuelles et la grille (ce qu'on planifie) ;
     'moves' = la variation des salaires bruts et le panneau des mouvements
               (ce qu'on simule).
   Sans `part`, la page se rend entière, comme avant. */
export default function HrPlanningPage({ activeTenant, year: yearProp, embedded, unit = 'chf', forExport = false, part = null,
                                         intent = null, onIntentConsumed = null }) {
  const { t } = useTranslation();
  const p = useP();
  const MONTHS = useMemo(() => Array.from({ length: 12 }, (_, i) => t(`common:months.${i + 1}`)), [t]);
  const MONTHS_FULL = useMemo(() => Array.from({ length: 12 }, (_, i) => t(`common:monthsFull.${i + 1}`)), [t]);
  const tenantId = activeTenant?.id;
  const months = useMemo(() => extraMonths(activeTenant),
    [activeTenant?.thirteenth_salary_month, activeTenant?.thirteenth_salary_month_2, activeTenant?.fourteenth_salary_month_13, activeTenant?.fourteenth_salary_month]);
  const year = yearProp ?? new Date().getFullYear();

  // Borne réel/forecast (état tenant, indépendant de l'année affichée).
  // firstOpen = 1ᵉʳ mois planifiable = mois suivant la dernière clôture.
  const closedThrough = activeTenant?.last_closed_period ? parseLocalDate(activeTenant.last_closed_period) : null;
  const firstOpen = closedThrough ? new Date(closedThrough.getFullYear(), closedThrough.getMonth() + 1, 1) : null;
  const yearClosed = !!firstOpen && year < firstOpen.getFullYear();           // année entière clôturée
  const isOpenMonth = (m) => !firstOpen || new Date(year, m, 1) >= firstOpen;  // mois ouvert dans l'année affichée
  // Même règle, année explicite : la frise roule sur N et N+1 (2026-08-22).
  const isOpenAt = (y, m) => !firstOpen || new Date(y, m, 1) >= firstOpen;
  // 1ᵉʳ mois ouvert dans l'année affichée (défaut de saisie / borne min picker)
  const planStartIdx = (firstOpen && year === firstOpen.getFullYear()) ? firstOpen.getMonth() : 0;
  const pickerMin = firstOpen ? `${firstOpen.getFullYear()}-${String(firstOpen.getMonth() + 1).padStart(2, '0')}` : undefined;
  // Années sélectionnables dans les colonnes De/À de la table mouvements :
  // du 1ᵉʳ mois planifiable (ou N-1 si pas de clôture) jusqu'à N+5.
  const moveMinYear = firstOpen ? firstOpen.getFullYear() : year - 1;
  const moveYears = [];
  for (let yy = moveMinYear; yy <= year + 5; yy += 1) moveYears.push(yy);

  const [contracts, setContracts] = useState([]);
  const [people, setPeople] = useState([]);
  const [lppRates, setLppRates] = useState([]);
  const [synth, setSynth] = useState(null);
  const [movements, setMovements] = useState([]);
  /* La LECTURE par défaut (2026-08-21) : le tableau à colonnes est bon pour
     saisir — une colonne par nature, un ＋ par colonne — mais il n'a pas d'axe
     du temps, donc chaque carte y réimprime ses dates. La frise le lui rend.
     On garde les deux : lire d'abord, saisir d'un clic. */
  /* Tableau par DÉFAUT : c'est l'écran de saisie, et on vient ici pour poser
     des mouvements. La frise sert à relire ce qu'on a posé — un clic. Le choix
     est retenu durablement (localStorage) : qui lit d'abord retrouve la frise
     au retour, sans la redemander. */
  /* La frise répond à DEUX questions. « Par personne » : qu'arrive-t-il à
     chacun — une ligne par collaborateur, le profil de taux en fond. « Par
     cause » : qu'ai-je décidé, et quand — une ligne par MOUVEMENT, regroupée
     par nature. Ce sont deux lectures du même fait, pas deux écrans. */
  const [moveGroup, setMoveGroup] = usePersistentValueLocal(`hr_planning_movegroup_${tenantId}`, 'person');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = usePersistentValue(`hr_planning_status_v2_${tenantId}`, 'all');
  /* Fenêtre d'années de la frise : la card porte la navigation de période
     (zone 6 du standard), la frise rend compte de ce qu'elle sait afficher. */
  const [tlOff, setTlOff] = useState(0);
  const [tlWindow, setTlWindow] = useState({ maxOff: 0, label: '', count: 0 });
  // 13e/14e salaire : Concentré (versé en entier sur son mois — historique) vs
  // Lissé (réparti à parts égales sur les mois actifs, sans pic).
  const [smoothExtras, setSmoothExtras] = usePersistentValue(`hr_planning_smooth13_${tenantId}`, false);
  // Table des mouvements : recherche par personne + modale unique (créer/modifier).
  const [formOpen, setFormOpen] = useState(null);   // null | { kind, initial, presetPersonId }
  /* Intention venue d'ailleurs (bouton « ＋ Mouvement » de la section) : on
     ouvre la modale à l'arrivée, puis on la consomme — sinon elle se
     rouvrirait à chaque rendu. Même mécanique que `PersonnelHub`. */
  useEffect(() => {
    if (!intent) return;
    if (intent.action === 'add') setFormOpen({ kind: intent.kind || 'hire', initial: null });
    if (onIntentConsumed) onIntentConsumed();
  }, [intent]);  // eslint-disable-line
  // Toggle inutile sans 13e/14e salaire dans les contrats — ne s'affiche que
  // s'il y a vraiment quelque chose à lisser.
  const hasExtraSalary = useMemo(() => contracts.some(c => Number(c.nb_of_salary || 12) > 12), [contracts]);
  const [search, setSearch] = useState('');
  /* Le graphique se replie (2026-08-29) : sur douze mois il tient la moitié de
     l'écran, et l'on vient souvent ici pour la seule liste des mouvements. Le
     choix est retenu. */
  const [chartOpen, setChartOpen] = usePersistentValueLocal(`hr_planning_chart_${tenantId}`, true);
  const sort = useTableSort(`hr_planning_sort_${tenantId}`, 'entry_date', 'desc');
  const { sortCol, sortDir, handleSort, sortRows, nameOrder } = sort;

  function refresh() {
    if (!tenantId) return;
    setLoading(true);
    Promise.all([
      listHrContracts(tenantId),
      listHrPeople(tenantId),
      listHrLppRates(tenantId),
      peopleCostSynthesis(tenantId, year, 'all'),
      listPayrollMovements(tenantId),
    ])
      .then(([c, p, l, s, m]) => {
        if (!c.error) setContracts(c.data || []);
        if (!p.error) setPeople(p.data || []);
        if (!l.error) setLppRates(l.data || []);
        if (!s.error) setSynth(s.data || s);
        if (!m.error) setMovements(m.data || []);
      })
      .finally(() => setLoading(false));
  }
  useEffect(refresh, [tenantId, year]); // eslint-disable-line

  // Taux employeur EFFECTIFS (charge réelle / brut), repris des totaux de la
  // synthèse People — pour que la baseline Planning égale exactement Personnel
  // actuel (taux par personne, pas le taux de repli moyen). CS = hors LPP.
  const brutSyn   = Number(synth?.total_effective_remuneration || 0);
  const socialSyn = Number(synth?.total_social_empr || 0);
  const lppSyn    = Number(synth?.total_lpp_empr || 0);
  const otherFees = Number(synth?.total_other_fees || 0);
  const csEmprRate = brutSyn > 0 ? ((socialSyn - lppSyn) / brutSyn) * 100 : Number(synth?.cs_fallback_rate_empr || 0);
  const lppEmprPct = brutSyn > 0 ? (lppSyn / brutSyn) * 100 : 0;
  const lppEmplPct = useMemo(() => {
    if (!lppRates.length) return 0;
    const sumRef = lppRates.reduce((a, r) => a + Number(r.reference_salary || 0), 0);
    const sumEmpl = lppRates.reduce((a, r) => a + Number(r.lpp_employee_chf || 0), 0);
    return sumRef > 0 ? (sumEmpl / sumRef) * 100 : 0;
  }, [lppRates]);
  const totalEmprRate = csEmprRate + lppEmprPct;

  // Extras annuels (compléments soumis CS) par personne, depuis la synthèse
  // People → intégrés au coût-par-EPT de la baseline (cf. addExtrasToCells).
  const extrasByPerson = useMemo(() => {
    const m = new Map();
    for (const r of (synth?.rows || [])) m.set(r.person_id, Number(r.bonus_variable || 0));
    return m;
  }, [synth]);

  // Index mouvements par personne (rate_change / departure rattachés).
  const movementsByPerson = useMemo(() => {
    const m = new Map();
    for (const mv of movements) {
      if (!mv.person_id) continue;
      if (!m.has(mv.person_id)) m.set(mv.person_id, []);
      m.get(mv.person_id).push(mv);
    }
    return m;
  }, [movements]);

  // Lignes personnes existantes (baseline contrats + overlay mouvements).
  const peopleRows = useMemo(() => {
    const yearStart = new Date(year, 0, 1);
    const yearEnd   = new Date(year, 11, 31);
    const byPerson = new Map();
    for (const c of contracts) {
      if (!c.person_id) continue;
      if (!byPerson.has(c.person_id)) byPerson.set(c.person_id, []);
      byPerson.get(c.person_id).push(c);
    }
    return people
      .filter(p => {
        if (p.entry_date && parseLocalDate(p.entry_date) > yearEnd) return false;
        if (p.termination_date && parseLocalDate(p.termination_date) < yearStart) return false;
        return true;
      })
      .map(p => {
        const pc = byPerson.get(p.id) || [];
        const ref = pickReferenceContract(pc);
        const ar = ref ? Number(ref.activity_rate || 0) : 0;
        const ms = ref ? Number(ref.monthly_salary || 0) : 0;
        const salary100 = (ref && ar > 0) ? ms / ar : 0;
        const base = buildBaseCells(p, pc, year);
        const nBaseActive = base.filter(b => b.kind === 'active').length;
        const annualExtras = extrasByPerson.get(p.id) || 0;
        // Cellules = base + 13e/14e (sur leur mois) + extras étalés au prorata
        // du taux. `cells` = avec mouvements ; `baseCells` = baseline (sans), pour
        // que Δ vs baseline ne capture QUE les mouvements (et pas les extras).
        const extraFn = smoothExtras ? smoothExtraSalariesToCells : addExtraSalariesToCells;
        const build = (mvs) => addExtrasToCells(
          extraFn(
            applyPersonMovements(base, salary100, mvs, year, isOpenMonth),
            ref, months,
          ),
          annualExtras, ar, nBaseActive,
        );
        return {
          ...p, synthetic: false,
          department: ref?.department || '',
          salary100,
          curRate: ar,
          cells: build(movementsByPerson.get(p.id) || []),
          baseCells: build([]),
          hasMv: (movementsByPerson.get(p.id) || []).length > 0,
          // Tri prénom / nom séparés (pastilles P/N) + filtre standard.
          first_name_sort: (p.first_name || '').toLowerCase(),
          last_name_sort:  (p.last_name  || '').toLowerCase(),
          is_active: personIsActive(pc),
          _search: buildSearchKey(p.first_name, p.last_name, ref?.department),
        };
      })
      .sort((a, b) => (b.entry_date || '').localeCompare(a.entry_date || ''));
  }, [people, contracts, year, movementsByPerson, extrasByPerson, months, activeTenant?.last_closed_period, smoothExtras]); // eslint-disable-line

  // Lignes synthétiques (mouvements sans person_id) actives sur l'année.
  const syntheticRows = useMemo(() => {
    return movements
      .filter(mv => !mv.person_id)
      .map(mv => buildSyntheticRow(mv, year, isOpenMonth))
      .filter(r => r.cells.some(c => c.gross !== 0)); // visible si actif dans l'année
  }, [movements, year, activeTenant?.last_closed_period]); // eslint-disable-line

  /* ── La frise roule sur N et N+1 (2026-08-22) ────────────────────────────
     Un contrat qui se termine en septembre N+1 était invisible : l'écran
     s'arrêtait au 31 décembre. Chez KeyIT, 3,6 EPT de baisse déjà signés ne
     se voyaient nulle part.

     Ces lignes ne portent QUE LE TAUX. Le brut de N+1 demanderait les extras
     de N+1, que la synthèse ne fournit pas pour une année future — les
     reprendre de N serait un repli silencieux. La frise n'en a pas besoin :
     elle affiche des pourcentages. */
  const TL_YEARS = useMemo(() => [year, year + 1], [year]);
  const rateCellsFor = (p, pc, salary100, mvs) => TL_YEARS.flatMap((y) =>
    applyPersonMovements(buildBaseCells(p, pc, y), salary100, mvs, y, (m) => isOpenAt(y, m)));

  const timelineRows = useMemo(() => {
    const byPerson = new Map();
    for (const c of contracts) {
      if (!c.person_id) continue;
      if (!byPerson.has(c.person_id)) byPerson.set(c.person_id, []);
      byPerson.get(c.person_id).push(c);
    }
    return peopleRows.map((r) => {
      const pc = byPerson.get(r.id) || [];
      const mvs = movementsByPerson.get(r.id) || [];
      return {
        ...r,
        cells: rateCellsFor(r, pc, r.salary100, mvs),
        baseCells: rateCellsFor(r, pc, r.salary100, []),
      };
    });
  }, [peopleRows, contracts, movementsByPerson, TL_YEARS, activeTenant?.last_closed_period]); // eslint-disable-line

  /* Lignes synthétiques SUR LA FENÊTRE, pas sur l'exercice : un mouvement daté
     de N+1 était écarté par le filtre d'année — il entre maintenant sur l'axe. */
  const timelineSynthetic = useMemo(() => movements
    .filter((mv) => !mv.person_id)
    .map((mv) => {
      const cells = TL_YEARS.flatMap((y) => buildSyntheticRow(mv, y, (m) => isOpenAt(y, m)).cells);
      return { id: mv.id, synthetic: true, mv, label: mv.label,
               department: mv.department || '', cells, baseCells: cells.map(() => ({ pct: 0 })) };
    })
    .filter((r) => r.cells.some((c) => c.pct !== 0)),
  [movements, TL_YEARS, activeTenant?.last_closed_period]); // eslint-disable-line

  /* Départements EXISTANTS — proposés (jamais imposés) à la saisie d'un
     mouvement non nominatif. Dérivés des contrats : un département absent des
     contrats n'a rien à proposer. */
  const depts = useMemo(
    () => [...new Set(peopleRows.map(r => r.department).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'fr')),
    [peopleRows],
  );

  const visiblePeople = useMemo(() => {
    let arr = peopleRows;
    if (statusFilter === 'active') arr = arr.filter(p => p.is_active);
    // Pas de sélecteur de département : la recherche couvre déjà nom,
    // prénom ET département (_search) — un filtre en double d'un champ qui
    // sait déjà le faire, c'est un contrôle de trop.
    const q = normalizeName(search.trim());
    if (q) arr = arr.filter(p => p._search.includes(q));
    return arr;
  }, [peopleRows, statusFilter, search]);

  /* Les lignes de la frise suivent le filtre ET le tri de la liste — placées
     APRÈS `visiblePeople` : un `const` lu avant sa déclaration n'est pas une
     variable manquante mais une zone morte temporelle, et l'écran casse au
     rendu (constat 2026-08-22). */
  const sortedTimeline = useMemo(() => {
    const keep = new Set(visiblePeople.map((x) => x.id));
    return sortRows(timelineRows.filter((r) => keep.has(r.id)));
  }, [timelineRows, visiblePeople, sortCol, sortDir]); // eslint-disable-line

  const sortedPeople = useMemo(
    () => sortRows(visiblePeople),
    [visiblePeople, sortCol, sortDir], // eslint-disable-line
  );

  const allRows = useMemo(() => [...visiblePeople, ...syntheticRows], [visiblePeople, syntheticRows]);

  // Affichage des mouvements : nominatifs d'abord (personne, ordre alphabétique),
  // puis les non-nominatifs (positions ouvertes / réductions générales) en bas.
  const peopleNameById = useMemo(() => {
    const m = new Map();
    for (const p of people) m.set(p.id, `${(p.last_name || '').toUpperCase()} ${p.first_name || ''}`.trim());
    return m;
  }, [people]);
  const sortedMovements = useMemo(() => {
    const typeRank = { hire: 0, cut: 1 };
    return [...movements].sort((a, b) => {
      const aNom = a.person_id ? 0 : 1;
      const bNom = b.person_id ? 0 : 1;
      if (aNom !== bNom) return aNom - bNom;
      if (aNom === 0) {
        const c = (peopleNameById.get(a.person_id) || '').localeCompare(peopleNameById.get(b.person_id) || '', 'fr');
        if (c !== 0) return c;
      } else {
        const c = (typeRank[logicalType(a)] ?? 9) - (typeRank[logicalType(b)] ?? 9);
        if (c !== 0) return c;
      }
      return String(a.start_month || '').localeCompare(String(b.start_month || ''));
    });
  }, [movements, peopleNameById]);

  // Masses mensuelles : baseline (contrats + extras, sans mouvements) vs
  // effectif (avec mouvements). Même base que Personnel actuel → la baseline
  // (sans mouvement) somme exactement au brut de la synthèse.
  const monthlyBase = useMemo(() =>
    ALL_M.map(m => visiblePeople.reduce((a, r) => a + (r.baseCells[m].gross || 0), 0)),
    [visiblePeople]);
  const monthlyEff = useMemo(() =>
    ALL_M.map(m => allRows.reduce((a, r) => a + (r.cells[m].gross || 0), 0)),
    [allRows]);
  const monthlyFte = useMemo(() =>
    ALL_M.map(m => allRows.reduce((a, r) => a + (r.cells[m].pct || 0) / 100, 0)),
    [allRows]);
  const monthlyHc = useMemo(() =>
    ALL_M.map(m => allRows.reduce((a, r) => {
      const pct = r.cells[m].pct || 0;
      return a + (pct > 0 ? 1 : pct < 0 ? -1 : 0);
    }, 0)),
    [allRows]);

  const annualBase = monthlyBase.reduce((a, n) => a + n, 0);
  const annualEff  = monthlyEff.reduce((a, n) => a + n, 0);
  const impact     = annualEff - annualBase;
  const avgMonthly = annualEff / 12;
  // Coût total employeur = brut chargé (CS + LPP) + frais de personnel — même
  // périmètre que Personnel actuel. Frais = montant baseline (ne suivent pas le
  // rythme de la paie ; les mouvements n'en portent pas).
  const totalCost  = annualEff * (1 + totalEmprRate / 100) + otherFees;
  const avgFte     = monthlyFte.reduce((a, n) => a + n, 0) / 12;

  // LPP : exacte (hr_lpp_rates, personnes existantes présentes) + estimée
  // (nouveaux postes = taux LPP moyen × masse annuelle). Étiquetée.
  const lppByPerson = useMemo(() => {
    const m = {};
    for (const r of lppRates) m[r.person_id] = (Number(r.lpp_employer_chf) || 0) + (Number(r.lpp_employee_chf) || 0);
    return m;
  }, [lppRates]);
  const lppExact = visiblePeople.reduce((a, p) => a + (lppByPerson[p.id] || 0), 0);
  const hiresAnnualGross = syntheticRows
    .filter(r => r.sign > 0)
    .reduce((a, r) => a + r.cells.reduce((s, c) => s + Math.max(0, c.gross), 0), 0);
  const lppEstimated = hiresAnnualGross * (lppEmprPct + lppEmplPct) / 100;
  const lppTotal = lppExact + lppEstimated;
  const m = (v) => formatMoney(v, unit, 0);          // montants (÷1000 si kCHF)
  const chfLbl = unit === 'kchf' ? 'kCHF' : 'CHF';


  // Mouvements antérieurs à la clôture (à promouvoir en contrat) — ignorés du calcul.

  const orphanCount = closedThrough
    ? movements.filter(mv => parseLocalDate(mv.start_month) <= closedThrough).length
    : 0;

  // ── CRUD mouvements ────────────────────────────────────────────────────
  async function addMovement(payload) {
    const r = await createPayrollMovement(tenantId, payload);
    if (r.error) setError(r.error);
    else setMovements(prev => [...prev, r.data]);
  }
  async function patchMv(mid, patch) {
    const r = await updatePayrollMovement(tenantId, mid, patch);
    if (r.error) setError(r.error);
    else setMovements(prev => prev.map(m => (m.id === mid ? r.data : m)));
  }
  async function removeMv(mid) {
    const r = await deletePayrollMovement(tenantId, mid);
    if (r.error) setError(r.error);
    else setMovements(prev => prev.filter(m => m.id !== mid));
  }
  // Défaut de saisie = 1ᵉʳ mois ouvert (jamais avant la clôture).
  // monthIdx optionnel : permet de créer le mouvement au mois cliqué sur le graphique
  // (sinon défaut = 1er mois planifiable). `?? ` capte undefined (month 0 = janvier reste valide).
  /* Frontière réel / planifiable DU GRAPHE : −1 sur une année entièrement
     close (aucun bouton d'ajout), 0 sur une année entièrement à venir, sinon
     le premier mois ouvert. `planStartIdx` ne sait pas dire « close » : il
     retombe à 0, ce qui ouvrirait la planification sur un mois déjà arrêté. */
  const chartSepIdx = !firstOpen ? 0
    : year > firstOpen.getFullYear() ? 0
    : year === firstOpen.getFullYear() ? firstOpen.getMonth()
    : -1;
  const addHire = (monthIdx) => addMovement({ movement_type: 'hire', start_month: ymToDateStr(year, monthIdx ?? planStartIdx), monthly_amount: DEFAULT_AMOUNT, fte: 1, label: p('Nouveau poste') });
  const addCut  = (monthIdx) => addMovement({ movement_type: 'departure', start_month: ymToDateStr(year, monthIdx ?? planStartIdx), monthly_amount: DEFAULT_AMOUNT, fte: 1, label: t('phrases:p_b2a060ca5d') });

  /* Ces trois aides et le regroupement par cause vivent AVANT le retour
     anticipé : `causeGroups` est un hook, et un hook posé après un `return`
     casse l'ordre des hooks au premier rendu sans client. */
  const personOf = (id) => people.find(pp => pp.id === id);
  // Période lisible d'un mouvement (« dès 1er sept. 2026 » / « du 1er juil.
  // 2026 au fin déc. 2027 »).
  const moveDateLabel = (mv, def) => {
    const sd = parseLocalDate(mv.start_month);
    const sLabel = `${p('1er')} ${MONTHS[sd.getMonth()]} ${sd.getFullYear()}`;
    if (def.end === false || !mv.end_month) return `${p('dès')} ${sLabel}`;
    const ed = parseLocalDate(mv.end_month);
    const eLabel = `${p('fin')} ${MONTHS[ed.getMonth()]} ${ed.getFullYear()}`;
    return `${p('du')} ${sLabel} ${p('au')} ${eLabel}`;
  };
  // Taux ou montant (signé pour une modification de salaire).
  const moveValueLabel = (mv, def) => {
    if (def.signed) {
      const v = Math.round(Number(mv.monthly_amount) || 0);
      return `${v >= 0 ? '+' : ''}${fmtChfInt(v)} CHF/m`;
    }
    if (def.amount) return `${fmtChfInt(mv.monthly_amount)} CHF/m`;
    if (def.rate) return mv.fte != null ? `${Math.round(Number(mv.fte) * 100)} %` : '—';
    return '—';
  };

  /* Vue « par cause » : un mouvement = une barre, posée sur ses mois. Les
     mouvements hors de l'exercice affiché sont écartés — une barre ne peut
     pas montrer une date qui n'est pas sur l'axe. */
  const CAUSE_ORDER = ['exit', 'rate_change', 'salary_change', 'hire', 'cut'];
  const CAUSE_COLOR = {
    exit: '--color-terracotta', rate_change: '--color-accent',
    salary_change: '--color-accent', hire: '--color-amber', cut: '--color-amber',
  };
  const causeGroups = useMemo(() => {
    /* Projection sur la FENÊTRE de la frise (N et N+1), plus sur le seul
       exercice : un mouvement daté de N+1 était écarté — il a maintenant sa
       place sur l'axe. */
    const span = TL_YEARS.length * 12;
    const idxOf = (d) => (d.getFullYear() - year) * 12 + d.getMonth();
    const spanOf = (mv) => {
      const sd = monthStartOf(parseLocalDate(mv.start_month));
      if (!sd) return null;
      const ed = mv.end_month ? monthStartOf(parseLocalDate(mv.end_month)) : null;
      const from = Math.max(0, idxOf(sd));
      const to = ed ? Math.min(span - 1, idxOf(ed)) : span - 1;
      if (from > span - 1 || to < 0 || to < from) return null;
      return [from, to];
    };
    // Les filtres de personne valent aussi ici : une ligne nominative suit sa
    // personne. Les mouvements globaux (position, réduction) restent toujours.
    const keep = new Set(visiblePeople.map(pp => pp.id));
    return CAUSE_ORDER.map((kind) => {
      const def = LOGICAL[kind] || {};
      const rows = sortedMovements
        .filter(mv => logicalType(mv) === kind)
        .filter(mv => !mv.person_id || keep.has(mv.person_id))
        .map((mv) => {
          const sp = spanOf(mv);
          if (!sp) return null;
          const person = personOf(mv.person_id);
          const row = peopleRows.find(r => r.id === mv.person_id);
          return {
            id: mv.id,
            name: person ? `${person.last_name || ''} ${person.first_name || ''}`.trim() : (mv.label || def.label),
            dept: (person ? row?.department : mv.department) || (person ? '—' : p('À définir')),
            detail: (def.amount || def.rate) ? moveValueLabel(mv, def) : moveDateLabel(mv, def),
            from: sp[0], to: sp[1],
          };
        })
        .filter(Boolean);
      return { key: kind, label: def.label, color: CAUSE_COLOR[kind], rows };
    }).filter(g => g.rows.length > 0);
  }, [sortedMovements, visiblePeople, peopleRows, year, TL_YEARS, people]);  // eslint-disable-line


  if (!tenantId) return <main className="hr-page"><div className="hr-page__empty">{p('Sélectionner une entreprise.')}</div></main>;

  const monthLabel = (i) => `${MONTHS[i]}-${String(year).slice(2)}`;

  // Table des mouvements en tableau de bord Kanban (2026-08-13) : une COLONNE
  // par type — la table ne sert QUE à ça, autant l'exploiter comme telle. Tri
  // par prénom dans les colonnes nominatives (chercher/vérifier qqn d'un coup
  // d'œil) ; par date pour les lignes globales (pas de nom à trier).
  const byFirstName = (a, b) => (personOf(a.person_id)?.first_name || '').localeCompare(personOf(b.person_id)?.first_name || '');
  const exitMoves = sortedMovements.filter(m => logicalType(m) === 'exit').sort(byFirstName);
  const rateChangeMoves = sortedMovements.filter(m => logicalType(m) === 'rate_change').sort(byFirstName);
  const salaryMoves = sortedMovements.filter(m => logicalType(m) === 'salary_change').sort(byFirstName);
  const hireMoves = sortedMovements.filter(m => logicalType(m) === 'hire');
  const cutMoves = sortedMovements.filter(m => logicalType(m) === 'cut');

  // Une tuile = un mouvement. Nom (ou libellé pour une ligne globale),
  // période + valeur en dessous, ✎ modifie (ouvre MoveFormModal), ✕ supprime.
  const renderMoveTile = (mv) => {
    const lt = logicalType(mv);
    const def = LOGICAL[lt] || {};
    const person = personOf(mv.person_id);
    const title = person ? `${person.first_name || ''} ${(person.last_name || '').toUpperCase()}`.trim() : (mv.label || def.label);
    return (
      <div className="hr-kanban__tile" key={mv.id} title={title}>
        <span className="hr-kanban__tile-name">{title}</span>
        <span className="hr-kanban__tile-sub">
          {moveDateLabel(mv, def)}{(def.amount || def.rate) && ` · ${moveValueLabel(mv, def)}`}
        </span>
        <span className="hr-kanban__tile-actions hr-page__no-print">
          <button title={p('Modifier')} onClick={() => setFormOpen({ kind: lt, initial: mv })}>✎</button>
          <button title={t('common:actions.delete')} onClick={() => removeMv(mv.id)}>×</button>
        </span>
      </div>
    );
  };

  // Une colonne du Kanban : en-tête (libellé + compteur + ＋), tuiles ou état vide.
  const renderMoveColumn = (kind, moves, accentVar) => (
    <div className="hr-kanban__col" key={kind}>
      <div className="hr-kanban__col-head" style={{ borderBottomColor: `var(${accentVar})` }}>
        <span className="hr-kanban__col-title">{LOGICAL[kind].label}</span>
        <span className="hr-kanban__col-count">{moves.length}</span>
        {!yearClosed && (
          <button className="hr-kanban__col-add hr-page__no-print" title={p('Ajouter')}
            onClick={() => setFormOpen({ kind, initial: null })}>＋</button>
        )}
      </div>
      <div className="hr-kanban__col-body">
        {moves.length === 0
          ? <div className="hr-kanban__empty">{p('Aucune')}</div>
          : moves.map(renderMoveTile)}
      </div>
    </div>
  );

  return (
    <main className={`hr-page${embedded ? ' is-embedded' : ''}`}>
      {!embedded && (
        <PageHeader
          title={p('Planification du personnel')}
        />
      )}

      {error && <div className="hr-page__error">{typeof error === 'string' ? error : (error.message || JSON.stringify(error))}</div>}
      {loading && <div className="hr-page__loading">{t('common:status.loading')}</div>}
      {!loading && (
        <>
          {yearClosed && (
            <div className="hr-plan-banner hr-plan-banner--closed hr-page__no-print">
              <strong>{t('phrases:p_11c8dd5c51', { year })}</strong>{t('phrases:p_a7483f2d5a', { month: MONTHS[firstOpen.getMonth()], startYear: firstOpen.getFullYear() })}
            </div>
          )}
          {orphanCount > 0 && (
            <div className="hr-plan-banner hr-plan-banner--warn hr-page__no-print">
              <span dangerouslySetInnerHTML={{ __html: t('phrases:p_6ff12717fa', { count: orphanCount, s: orphanCount > 1 ? 's' : '' }) }} />
            </div>
          )}
          {/* Tuiles de synthèse PLATES, pleine largeur (2026-08-22). */}
          {part !== 'moves' && <div className="hr-top-row hr-top-row--tiles">
            <div className="hr-synth hr-synth--side">
              <div className="hr-synth__head">{t('phrases:p_d17cfed89c', { year })}</div>
              <div className="hr-synth__grid hr-synth__grid--6">
                <KpiCell label={t('phrases:p_1842b6b677', { year })} value={m(annualEff)} suffix={chfLbl} hint={impact !== 0 ? t('phrases:p_5a07cc4d99', { delta: `${impact > 0 ? '+' : ''}${m(impact)}` }) : p('baseline contrats')} />
                <KpiCell label={p('Moyenne mensuelle')} value={m(avgMonthly)} suffix={chfLbl} hint={`${MONTHS[0]} → ${MONTHS[11]}`} />
                <KpiCell label={t('phrases:p_4ff08f6137')} value={m(totalCost)} suffix={chfLbl} hint={`+${formatNumber(totalEmprRate, 1)}% (CS ${formatNumber(csEmprRate, 1)} + LPP ${formatNumber(lppEmprPct, 1)})`} accent="primary" />
                <KpiCell label={t('phrases:p_3de41d3cf0')} value={m(lppTotal)} suffix={chfLbl} hint={lppEstimated > 0 ? t('phrases:p_23134d2a8b', { amount: m(lppEstimated) }) : p('contrats — exacte')} />
                <KpiCell label={p('FTE moyen annuel')} value={formatNumber(avgFte, 1)} hint={p('moyenne 12 mois')} accent="sage" />
                <KpiCell label={p('Δ vs baseline')} value={`${impact >= 0 ? '+' : ''}${m(impact)}`} suffix={chfLbl} hint={annualBase ? `${impact >= 0 ? '+' : ''}${formatNumber(impact / annualBase * 100, 1)}%` : '—'} accent={impact >= 0 ? 'primary' : 'terra'} />
              </div>
            </div>

          </div>}

          {/* Variation des salaires bruts, EN DEUXIÈME CARD (2026-08-26).

             Le graphique était parti en « Masse salariale planifiée » le
             2026-08-22 parce qu'il tenait la moitié de la largeur À CÔTÉ des
             tuiles et écrasait la frise. Il revient PLEINE LARGEUR, sous
             elles : le reproche portait sur le partage de ligne, pas sur la
             présence.

             Ses deux séries sont celles de la card du dessus (`monthlyBase`,
             contrats seuls, et `monthlyEff`, mouvements compris) : une card
             dont les tuiles et le graphe sortiraient de deux calculs se
             contredirait à la première divergence. */}
          {part !== 'plan' && <PayrollVariationChart
            gross={monthlyEff}
            base={monthlyBase}
            monthLabels={MONTHS}
            year={year}
            sepIdx={chartSepIdx}
            canPlan={chartSepIdx >= 0}
            onAddHire={addHire}
            onAddCut={addCut}
            collapsed={!chartOpen}
            aside={(
              <PeristyleToggle
                open={chartOpen}
                onToggle={() => setChartOpen(v => !v)}
                title={chartOpen ? p('Agréger') : p('Détailler')}
              />
            )}
          />}

          {/* Panneau des mouvements — standard de carte : entête = comment je
              regarde, toolbar = ce que je regarde.

              La bascule Tableau | Frise a disparu le 2026-08-27 : chaque
              ONGLET porte sa vue. « Mouvements » montre les colonnes de
              simulation, « Planning des effectifs » la frise. Choisir deux
              fois la même chose, par un onglet puis par une bascule, faisait
              deux chemins vers un seul écran. */}
          <CardShell
            className="hr-page__no-print"
            flush
            title={part === 'plan'
              ? t('phrases:p_052301441f')
              : p('Simuler les mouvements non actés / contractualisés')}
            subtitle={firstOpen ? (
              <>
                {p("Réel clôturé jusqu'à")} <strong>{MONTHS_FULL[closedThrough.getMonth()]} {closedThrough.getFullYear()}</strong>
                {' : '}{p('planification possible dès')} <strong>{MONTHS_FULL[firstOpen.getMonth()]} {firstOpen.getFullYear()}</strong>.
              </>
            ) : null}
            /* Plus de « Tout effacer » (2026-08-29) : un clic effaçait tous les
               mouvements du client, sans retour possible, à côté de contrôles
               d'affichage inoffensifs. Chaque mouvement se supprime depuis sa
               propre modale. */
            viewControls={part === 'plan' ? (
              <SegmentedToggle
                compact
                label={p('Grouper')} value={moveGroup} onChange={setMoveGroup}
                options={[
                  { value: 'person', label: p('Par personne') },
                  { value: 'dept', label: p('Par département') },
                  { value: 'cause', label: p('Par cause') },
                ]}
              />
            ) : null}
            filters={(
              <>
                <FilterIcon />
                <SegmentedToggle
                  compact
                  label={p('Statut')} value={statusFilter} onChange={setStatusFilter}
                  options={[
                    { value: 'all', label: p('Tous') },
                    { value: 'active', label: p('Actifs'), tone: 'alert' },
                  ]}
                />
                <FilterCount
                  count={(statusFilter === 'active' ? 1 : 0) + (search.trim() ? 1 : 0)}
                  onClear={() => { setStatusFilter('all'); setSearch(''); }}
                />
              </>
            )}
            /* Les flèches n'apparaissent que si la frise ne tient PAS en entier
               à l'écran (2026-08-29) : quand les deux années tiennent, elles
               étaient là, grisées, sans rien à faire. */
            scope={(part === 'plan' && tlWindow.maxOff > 0) ? (
              <PeriodNav
                label={tlWindow.label}
                onPrev={() => setTlOff(o => Math.max(0, o - 1))}
                onNext={() => setTlOff(o => Math.min(tlWindow.maxOff, o + 1))}
                prevDisabled={tlOff <= 0}
                nextDisabled={tlOff >= tlWindow.maxOff}
              />
            ) : null}
            search={<SearchField value={search} onChange={setSearch} placeholder={p('Rechercher…')} />}
            /* La barre de filtres se replie, comme partout ailleurs : l'entonnoir
               de l'entête l'ouvre et la referme, et retient ce choix. */
            filterCount={(statusFilter === 'active' ? 1 : 0) + (search.trim() ? 1 : 0)}
            collapsible storageKey={part === 'plan' ? 'people-plan' : 'people-moves'}
          >
            {part === 'plan' ? (
              <PlanningTimeline
                rows={sortedTimeline} syntheticRows={timelineSynthetic} years={TL_YEARS}
                closedThrough={closedThrough}
                sortCol={sortCol} sortDir={sortDir} onSort={handleSort}
                group={moveGroup} causeGroups={causeGroups}
                rowAction={(row) => (
                  <RowMoveMenu
                    disabled={yearClosed}
                    options={[
                      { key: 'rate_change', label: p('Changement de taux') },
                      { key: 'exit', label: p('Fin de contrat') },
                      { key: 'salary_change', label: p('Modification du salaire') },
                    ]}
                    onPick={(kind) => setFormOpen({ kind, initial: null, presetPersonId: row.id })}
                  />
                )}
                syntheticAction={(row) => (
                  <RowMoveMenu
                    disabled={yearClosed}
                    title={p('Modifier')}
                    options={[
                      { key: 'edit', label: p('Modifier') },
                      { key: 'delete', label: t('common:actions.delete') },
                    ]}
                    onPick={(k) => (k === 'edit'
                      ? setFormOpen({ kind: logicalType(row.mv), initial: row.mv })
                      : removeMv(row.mv.id))}
                  />
                )}
                deptAction={(d) => (yearClosed ? null : (
                  <span className="pt-plus">
                    <button type="button" title={p('Poste vacant dans ce département')}
                      onClick={() => setFormOpen({ kind: 'hire', initial: null, presetDept: d })}>＋</button>
                    <button type="button" title={p('Réduction dans ce département')}
                      onClick={() => setFormOpen({ kind: 'cut', initial: null, presetDept: d })}>−</button>
                  </span>
                ))}
                addSynthetic={!yearClosed && (
                  <RowMoveMenu
                    icon="＋"
                    title={p('Ajouter')}
                    options={[
                      { key: 'hire', label: LOGICAL.hire.label },
                      { key: 'cut', label: LOGICAL.cut.label },
                    ]}
                    onPick={(kind) => setFormOpen({ kind, initial: null })}
                  />
                )}
                windowOffset={tlOff} onWindowOffset={setTlOff} onWindow={setTlWindow}
              />
            ) : (
            <>

            <div className="hr-kanban is-no-export" data-tour="headcount-moves-columns">
              {renderMoveColumn('exit', exitMoves, '--color-terracotta')}
              {renderMoveColumn('rate_change', rateChangeMoves, '--color-accent')}
              {renderMoveColumn('salary_change', salaryMoves, '--color-accent')}
              {renderMoveColumn('hire', hireMoves, '--color-amber')}
              {renderMoveColumn('cut', cutMoves, '--color-amber')}
            </div>
            </>
            )}
          </CardShell>

          {/* Grille d'évolution — RETIRÉE DE L'ÉCRAN (2026-08-21 au soir) :
             elle redisait ce que la frise montre déjà, en plus long. Elle ne
             se monte plus que pour l'export Excel, dont elle reste la seule
             source tabulaire de l'onglet Effectifs (taux mensuels, totaux FTE
             et headcount). Supprimer le composant aurait vidé la feuille. */}
          {forExport && (
          <div className="hr-evol">
            <div className="hr-evol__head">
              <div className="hr-evol__title">
                ÉVOLUTION DU PERSONNEL · {year}
                <span className="hr-evol__hint">
                  {p('contrats + overlay mouvements · ✎ = planifier un mouvement · lignes synthétiques = positions / réductions')}
                </span>
              </div>
            </div>
            <div className="hr-evol__filters">
              <PersonFilterBar
                search={search}
                onSearch={setSearch}
                status={statusFilter}
                onStatus={setStatusFilter}
              />
            </div>
            <div className="hr-evol__table-wrap">
              <table className="hr-evol__table" data-export-name={t('phrases:p_052301441f')}>
                <thead>
                  <tr>
                    {SORT_COLUMNS.map(col => {
                      if (col.personSort) {
                        return <PersonTh key={col.key} sort={sort} className="hr-evol__sort-th" />;
                      }
                      const isSorted = sortCol === col.key;
                      const cls = `hr-evol__sort-th${col.align === 'right' ? ' hr-evol__num' : ''}${isSorted ? ' is-sorted' : ''}${col.detail ? ' hr-evol__col-detail' : ''}`;
                      return (
                        <th key={col.key} className={cls} onClick={() => handleSort(col.key)} title={p('Cliquer pour trier')}>
                          <span>{col.label}</span>
                          <span className="hr-evol__sort-ind">{isSorted ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}</span>
                        </th>
                      );
                    })}
                    {ALL_M.map(i => <th key={i} className="hr-evol__mcol">{monthLabel(i)}</th>)}
                    <th className="hr-evol__mcol hr-page__no-print" />
                  </tr>
                </thead>
                <tbody>
                  {sortedPeople.map(person => (
                    <tr key={person.id} className={person.hasMv ? 'hr-evol__row--planned' : ''}>
                      <td className="hr-evol__name"><PersonName firstName={person.first_name} lastName={person.last_name} order={nameOrder} /></td>
                      <td className={`hr-evol__col-detail ${person.department ? '' : 'hr-page__muted'}`}>{person.department || '—'}</td>
                      <td className="hr-evol__num hr-evol__col-detail">{person.salary100 > 0 ? fmtChfInt(person.salary100) : '—'}</td>
                      <td className="hr-evol__col-detail">{fmtMonthYear(person.entry_date)}</td>
                      <td className="hr-evol__col-detail">{fmtMonthYear(person.termination_date)}</td>
                      {person.cells.map((cell, i) => <GridCell key={i} cell={cell} month={i} year={year} />)}
                      <td className="hr-page__no-print" style={{ whiteSpace: 'nowrap' }}>
                        <RowMoveMenu
                          disabled={yearClosed}
                          options={[
                            { key: 'rate_change', label: p('Changement de taux') },
                            { key: 'exit', label: p('Fin de contrat') },
                            { key: 'salary_change', label: p('Modification du salaire') },
                          ]}
                          onPick={(kind) => setFormOpen({ kind, initial: null, presetPersonId: person.id })}
                        />
                      </td>
                    </tr>
                  ))}
                  {syntheticRows.map(r => (
                    <tr key={r.id} className={`hr-evol__row--synthetic ${r.sign > 0 ? 'is-hire' : 'is-cut'}`}>
                      <td className="hr-evol__name">{r.sign > 0 ? '＋ ' : '－ '}{r.label || (r.sign > 0 ? p('Nouveau poste') : p('Réduction générale'))}</td>
                      <td className="hr-evol__col-detail hr-page__muted">{r.mv.notes || '—'}</td>
                      <td className="hr-evol__num hr-evol__col-detail">{r.salary100 > 0 ? fmtChfInt(r.salary100) : '—'}</td>
                      <td className="hr-evol__col-detail">{fmtMonthYear(r.mv.start_month)}</td>
                      <td className="hr-evol__col-detail">{fmtMonthYear(r.mv.end_month)}</td>
                      {r.cells.map((cell, i) => <GridCell key={i} cell={cell} month={i} year={year} />)}
                      <td className="hr-page__no-print">
                        <button className="hr-evol__rowbtn" title={t('common:actions.delete')} onClick={() => removeMv(r.id)}>×</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="hr-evol__total-row hr-evol__total-row--fte">
                    <th scope="row" colSpan={5} className="hr-evol__total-label">
                      <span className="hr-evol__total-title">{p('Total FTE')}</span>
                      <span className="hr-evol__total-avg">moyenne {formatNumber(avgFte, 1)}</span>
                    </th>
                    {monthlyFte.map((v, i) => <td key={i} className="hr-evol__total-cell">{formatNumber(v, 1)}</td>)}
                    <td className="hr-page__no-print" />
                  </tr>
                  <tr className="hr-evol__total-row hr-evol__total-row--hc">
                    <th scope="row" colSpan={5} className="hr-evol__total-label">
                      <span className="hr-evol__total-title">Headcount net</span>
                    </th>
                    {monthlyHc.map((n, i) => <td key={i} className="hr-evol__total-cell">{n}</td>)}
                    <td className="hr-page__no-print" />
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
          )}

        </>
      )}
      {formOpen && (
        <MoveFormModal
          kind={formOpen.kind}
          initial={formOpen.initial}
          presetPersonId={formOpen.presetPersonId}
          presetDept={formOpen.presetDept}
          people={sortedPeople}
          depts={depts}
          moveYears={moveYears}
          firstOpen={firstOpen}
          defaultStartMonth={ymToDateStr(year, planStartIdx)}
          onCancel={() => setFormOpen(null)}
          onSubmit={async (payload) => {
            if (formOpen.initial) await patchMv(formOpen.initial.id, payload);
            else await addMovement(payload);
            setFormOpen(null);
          }}
        />
      )}
    </main>
  );
}

// Menu déroulant « ✎ » sur la ligne d'UNE personne (déjà connue, grille
// Évolution du personnel) : un seul bouton, les 2 mouvements possibles pour
// elle en options — ouvre MoveFormModal avec la personne déjà posée.
function RowMoveMenu({ options, onPick, disabled, icon = '✎', title = null }) {
  const [open, setOpen] = useState(false);
  // Position à l'ouverture (viewport) : le menu sort en PORTAL, ancré au
  // bouton. En absolu dans la ligne, il se faisait couper par le conteneur à
  // défilement de la frise dès que la liste était courte — un menu de trois
  // options coupé à la deuxième (constat 2026-08-21).
  const [pos, setPos] = useState(null);
  const ref = useRef(null);
  const popRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (ref.current && ref.current.contains(e.target)) return;
      if (popRef.current && popRef.current.contains(e.target)) return;
      setOpen(false);
    };
    // L'ancre bouge sous un menu fixe : on ferme plutôt que suivre.
    const onAway = () => setOpen(false);
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('scroll', onAway, true);
    window.addEventListener('resize', onAway);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('scroll', onAway, true);
      window.removeEventListener('resize', onAway);
    };
  }, [open]);
  if (disabled) return null;
  const toggle = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    // Ne jamais dépasser le bord droit (le ✎ de la grille est en bout de ligne).
    setPos({ top: r.bottom + 4, left: Math.min(r.left, window.innerWidth - 200) });
    setOpen((v) => !v);
  };
  return (
    <span className="hr-movemenu" ref={ref}>
      <button type="button" className="hr-evol__rowbtn" title={title || p('Planifier un mouvement')}
        onClick={toggle}>{icon}</button>
      {open && pos && createPortal(
        <div ref={popRef} className="hr-movemenu__pop hr-movemenu__pop--row"
             style={{ position: 'fixed', top: pos.top, left: pos.left, right: 'auto' }}>
          {options.map((o) => (
            <button key={o.key} type="button" className="hr-movemenu__item"
              onClick={() => { setOpen(false); onPick(o.key); }}>{o.label}</button>
          ))}
        </div>,
        document.body,
      )}
    </span>
  );
}

// ── MoveFormModal ────────────────────────────────────────────────────────
// UNE seule modale pour créer ET modifier un mouvement — décision 2026-08-13
// (retours : trop de boutons/champs éditables partout dans la table ; « un
// stylo pour modifier suffit »). Les champs affichés dépendent du type
// (LOGICAL[kind]) : personne si `person`, taux si `rate`, montant si
// `amount` (signé si `signed` — Modification du salaire), fin si `end`.
function MoveFormModal({ kind, initial, presetPersonId, presetDept, people, depts = [], moveYears, firstOpen, defaultStartMonth, onSubmit, onCancel }) {
  const def = LOGICAL[kind] || {};
  const [personId, setPersonId] = useState(initial?.person_id || presetPersonId || '');
  /* Département d'une ligne NON nominative (2026-08-22). Une ligne nominative
     tient le sien de son contrat ; une position ouverte n'en avait aucun —
     impossible alors de la valoriser au coût de référence d'un département.
     Vide reste permis : l'écran affiche « À définir » plutôt qu'un département
     attribué au hasard. */
  const [department, setDepartment] = useState(initial?.department || presetDept || '');
  const [q, setQ] = useState('');
  const [label, setLabel] = useState(initial?.label || '');
  // Création (pas de `initial`) : démarrer sur le 1ᵉʳ mois planifiable — sinon
  // un <select> contrôlé à valeur vide affiche un mois arbitraire du
  // navigateur (constat 2026-08-13 : « ne fonctionne qu'à partir d'août »
  // alors que juillet était le 1ᵉʳ mois ouvert).
  const [startMonth, setStartMonth] = useState(initial?.start_month || defaultStartMonth || null);
  const [endMonth, setEndMonth] = useState(initial?.end_month || null);
  const [ratePct, setRatePct] = useState(initial?.fte != null ? Math.round(Number(initial.fte) * 100) : (def.rate ? 100 : ''));
  const [amount, setAmount] = useState(initial?.monthly_amount != null ? String(Math.round(Number(initial.monthly_amount))) : '');

  const filtered = def.person
    ? people.filter((pp) => normalizeName(`${pp.last_name || ''} ${pp.first_name || ''}`).includes(normalizeName(q)))
    : [];
  const selectedPerson = people.find((pp) => pp.id === personId);
  const canSubmit = (!def.person || personId) && startMonth;

  const submit = () => {
    if (!canSubmit) return;
    const payload = {
      movement_type: def.mt,
      start_month: startMonth,
      end_month: def.end ? endMonth : null,
      label: label || null,
    };
    if (def.person) payload.person_id = personId;
    else payload.department = department || null;
    if (def.rate) payload.fte = ratePct === '' ? null : Number(ratePct) / 100;
    if (def.amount) payload.monthly_amount = amount === '' || amount === '-' ? 0 : Number(amount);
    onSubmit(payload);
  };

  return (
    <div className="hr-modal__overlay">
      <div className="hr-modal" onClick={(e) => e.stopPropagation()}>
        <div className="hr-modal__header">
          <span className="hr-modal__title">{def.label}</span>
          <button className="hr-modal__close" onClick={onCancel}>✕</button>
        </div>
        <div className="hr-modal__body" style={{ display: 'grid', gap: 12 }}>
          {def.person && (
            <div className="hr-modal__field">
              <label className="hr-modal__label">{p('Collaborateur')} *</label>
              {selectedPerson ? (
                <div className="hr-movemenu__chosen">
                  <span>{(selectedPerson.last_name || '').toUpperCase()} {selectedPerson.first_name || ''}</span>
                  <button type="button" onClick={() => setPersonId('')}>{p('changer')}</button>
                </div>
              ) : (
                <>
                  <input autoFocus className="hr-modal__input" placeholder={p('Rechercher…')}
                    value={q} onChange={(e) => setQ(e.target.value)} />
                  <div className="hr-movemenu__list">
                    {filtered.slice(0, 8).map((pp) => (
                      <button key={pp.id} type="button" className="hr-movemenu__item"
                        onClick={() => { setPersonId(pp.id); setQ(''); }}>
                        {(pp.last_name || '').toUpperCase()} {pp.first_name || ''}
                      </button>
                    ))}
                    {q && filtered.length === 0 && <div className="hr-movemenu__empty">{p('Aucun résultat')}</div>}
                  </div>
                </>
              )}
            </div>
          )}
          <div className="hr-modal__field">
            <label className="hr-modal__label">{tp('phrases:p_f5485bbae8')}</label>
            <input className="hr-modal__input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder={def.label} />
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <div className="hr-modal__field" style={{ flex: '1 1 140px' }}>
              <label className="hr-modal__label">{def.end ? (endMonth ? p('Du') : p('Dès')) : p('Dès')}</label>
              <MonthYearPicker value={startMonth} years={moveYears} dayPrefix="start"
                minYear={firstOpen ? firstOpen.getFullYear() : null} minMonth={firstOpen ? firstOpen.getMonth() : 0}
                onChange={(v) => { setStartMonth(v); if (endMonth && v && v > endMonth) setEndMonth(addMonthsStr(v, 1)); }} />
            </div>
            {def.end && (
              <div className="hr-modal__field" style={{ flex: '1 1 140px' }}>
                <label className="hr-modal__label">{p('Au')} <span className="hr-modal__hint">{p('(vide = permanent)')}</span></label>
                <MonthYearPicker value={endMonth} years={moveYears} allowEmpty dayPrefix="end"
                  defaultYear={startMonth ? Number(startMonth.slice(0, 4)) : undefined}
                  minYear={firstOpen ? firstOpen.getFullYear() : null} minMonth={firstOpen ? firstOpen.getMonth() : 0}
                  onChange={(v) => setEndMonth((v && startMonth && v <= startMonth) ? addMonthsStr(startMonth, 1) : v)} />
              </div>
            )}
            {!def.person && (
              <div className="hr-modal__field" style={{ flex: '1 1 170px' }}>
                <label className="hr-modal__label">
                  {p('Département')} <span className="hr-modal__hint">{p('(vide = à définir)')}</span>
                </label>
                <input className="hr-modal__input" list="hr-move-depts" value={department}
                  placeholder={p('À définir')}
                  onChange={(e) => setDepartment(e.target.value)} />
                <datalist id="hr-move-depts">
                  {depts.map((d) => <option key={d} value={d} />)}
                </datalist>
              </div>
            )}
            {def.rate && (
              <div className="hr-modal__field" style={{ flex: '1 1 100px' }}>
                <label className="hr-modal__label">{p('Taux')} <span className="hr-modal__hint">(%)</span></label>
                <input className="hr-modal__input" type="number" step="5" value={ratePct}
                  onChange={(e) => setRatePct(e.target.value)} />
              </div>
            )}
            {def.amount && (
              <div className="hr-modal__field" style={{ flex: '1 1 140px' }}>
                <label className="hr-modal__label">
                  {def.signed ? p('Montant (+/− CHF/mois)') : p('Montant (CHF/mois)')}
                </label>
                <input className="hr-modal__input" type="text" inputMode={def.signed ? 'text' : 'numeric'} value={amount}
                  onChange={(e) => setAmount(e.target.value.replace(def.signed ? /[^-\d]/g : /[^\d]/g, '').replace(/(?!^)-/g, ''))} />
              </div>
            )}
          </div>
        </div>
        <div className="hr-modal__footer">
          <button className="hr-modal__btn" onClick={onCancel}>{p('Annuler')}</button>
          <button className="hr-modal__btn hr-modal__btn--primary" disabled={!canSubmit} onClick={submit}>
            {initial ? p('Enregistrer') : p('Créer')}
          </button>
        </div>
      </div>
    </div>
  );
}

// Sélecteur mois + année (deux listes) — remplace input type=month pour garantir
// le choix de l'année sur tous les navigateurs (Safari ne gère pas type=month).
// allowEmpty → option « — » sur le mois = pas de borne (fin ouverte).
// dayPrefix 'start'|'end' → précise que la date porte sur le 1er jour du mois
// (borne de début, toujours day=1 en base) ou sa fin (borne de fin, incluse).
function MonthYearPicker({ value, onChange, years, allowEmpty, defaultYear, minYear, minMonth = 0, disabled = false, dayPrefix }) {
  const { t } = useTranslation();
  const p = useP();
  const MONTHS = useMemo(() => {
    const base = Array.from({ length: 12 }, (_, i) => t(`common:months.${i + 1}`));
    if (dayPrefix === 'start') return base.map((m) => `${p('1er')} ${m}`);
    if (dayPrefix === 'end') return base.map((m) => `${p('fin')} ${m}`);
    return base;
  }, [t, dayPrefix]); // eslint-disable-line
  const cur = value ? parseLocalDate(value) : null;
  const selM = cur ? cur.getMonth() : '';
  const selY = cur ? cur.getFullYear() : (defaultYear ?? years[0]);
  const yearOpts = years.includes(selY) ? years : [...years, selY].sort((a, b) => a - b);
  const emit = (mm, yy) => {
    if (mm === '' || mm == null) { onChange(null); return; }
    onChange(ymToDateStr(Number(yy), Number(mm)));
  };
  return (
    <span className="hr-moves__my">
      <select value={selM} disabled={disabled} onChange={e => emit(e.target.value, selY)}>
        {allowEmpty && <option value="">—</option>}
        {MONTHS.map((lbl, i) => (
          <option key={i} value={i} disabled={minYear != null && Number(selY) === minYear && i < minMonth}>{lbl}</option>
        ))}
      </select>
      <select value={selY} disabled={disabled} onChange={e => emit(selM, Number(e.target.value))}>
        {yearOpts.map(y => <option key={y} value={y} disabled={minYear != null && y < minYear}>{y}</option>)}
      </select>
    </span>
  );
}

// Cellule de grille : taux coloré (active/rate/hire), borne (before/after/none),
// départ planifié (exit) et réduction (cut, négatif).
function GridCell({ cell, month, year }) {
  const { t } = useTranslation();
  const MONTHS = useMemo(() => Array.from({ length: 12 }, (_, i) => t(`common:months.${i + 1}`)), [t]);
  let style;
  if (cell.kind === 'active' || cell.kind === 'rate') style = fteColor(cell.pct);
  else if (cell.kind === 'hire') style = fteColor(cell.pct);
  else if (cell.kind === 'cut') style = { background: 'var(--terracotta-light)', color: 'var(--terracotta-dark)' };
  else style = cellFteStyle(cell.kind, cell.pct);
  if (cell.kind === 'rate') style = { ...style, outline: '1.5px dashed var(--color-primary, #639922)', outlineOffset: -2 };
  if (cell.kind === 'exit') style = { background: 'var(--terracotta-light)', color: 'var(--terracotta-dark)', fontStyle: 'italic' };
  const empty = cell.pct === 0 && (cell.kind === 'hire' || cell.kind === 'cut');
  return (
    <td className={`hr-evol__cell hr-evol__cell--${cell.kind}`} style={empty ? undefined : style}
      title={`${cell.pct}% — ${MONTHS[month]} ${year}`}>
      {empty ? '' : <>{cell.pct}<span className="hr-evol__pct-sign">%</span></>}
    </td>
  );
}

function KpiCell({ label, value, hint, accent, negative, suffix }) {
  return (
    <div className={`hr-kpi ${accent ? `hr-kpi--${accent}` : ''}`}>
      <div className="hr-kpi__label">{label}</div>
      <div className={`hr-kpi__value${negative ? ' hr-kpi__value--negative' : ''}`}>
        {value}{suffix && <small className="hr-kpi__unit"> {suffix}</small>}
      </div>
      {hint && <div className="hr-kpi__hint">{hint}</div>}
    </div>
  );
}
